// ============================================================
// SecaoAvaliacaoCoord.js — o relatório da avaliação de fim de ciclo
//
// A leitura da coordenação sobre o que os atletas responderam. Segue o
// mesmo desenho dos outros relatórios do painel: um cartão de configurar
// em cima, os números do recorte, um gráfico de barras e os botões de
// Excel e PDF no cabeçalho do resultado.
//
// A contagem não é feita aqui. Ela vem de apurar(), em avaliacaoCiclo.js,
// que é a mesma função que a planilha e o PDF usam, pelo mesmo motivo que
// o relatório de frequência chama calcularFrequencia em vez de somar na
// tela: três lugares contando do seu jeito é três números diferentes na
// reunião.
//
// O técnico não chega aqui. A aba não aparece para ele e as regras do
// Firestore recusam a leitura, porque duas perguntas da seção "Ambiente e
// equipe" avaliam justamente ele.
// ============================================================

import { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from "recharts";
import { carregarAvaliacoesDoCiclo, carregarAtletas } from "../firebase";
import { normalizarModalidade } from "../calculo";
import { useNomeNucleo } from "../NucleosContexto";
import { gerarExcelAvaliacao } from "../excelAvaliacao";
import { gerarPDFAvaliacaoCiclo } from "../pdfRelatorio";
import { CICLO, CICLO_ROTULO, SECOES, apurar, prazoEmBR } from "../avaliacaoCiclo";
import {
  AZUL, ACAO, TEXTO, CINZA_ESC, BRANCO, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO,
  OK, ATENCAO, RUIM, NEUTRO_CL, BORDA_FORTE,
} from "../tema";
import {
  Card, Btn, Sel, StatusBox, Estatistica, FileiraDeEstatisticas, Carregando, Vazio,
} from "../componentes";
import useIsMobile from "../useIsMobile";

// A cor de uma opção sai da POSIÇÃO dela na escala, não do texto.
//
// Todas as escalas do questionário estão ordenadas da melhor para a pior
// ("Excelente, Boa, Regular, Ruim"), então a primeira é verde e a última é
// vermelha em qualquer pergunta. Uma tabela de palavras precisaria crescer
// a cada pergunta nova, e erraria calada na primeira que esquecessem.
//
// A mesma regra está no PDF, de propósito: o documento impresso e a tela
// precisam pintar a mesma resposta da mesma cor.
export function corDaOpcao(opcoes, opcao) {
  const o = opcoes.find(x => x.opcao === opcao);
  if (!o || o.neutra) return BORDA_FORTE;
  const julgadas = opcoes.filter(x => !x.neutra);
  const i = julgadas.findIndex(x => x.opcao === opcao);
  const n = julgadas.length;
  if (i < 0 || n <= 1) return AZUL;
  const posicao = i / (n - 1);
  if (posicao <= 0.01) return OK;
  if (posicao < 0.5) return AZUL;
  if (posicao < 0.99) return ATENCAO;
  return RUIM;
}

export default function SecaoAvaliacaoCoord({ userNucleo }) {
  const isMobile = useIsMobile();
  const nomeNucleo = useNomeNucleo();
  const nucleo = userNucleo || "";

  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [atletas, setAtletas] = useState([]);
  const [modalidade, setModalidade] = useState("");
  const [gerando, setGerando] = useState(null); // "excel" | "pdf" | null

  useEffect(() => {
    if (!nucleo) return;
    let ativo = true;
    setCarregando(true);
    setStatus(null);
    (async () => {
      try {
        // As duas leituras juntas: as respostas e o cadastro. Sem o
        // cadastro não há denominador, e "18 responderam" sem o total não
        // diz se a coleta foi boa ou se faltou metade do núcleo.
        const [respostas, pessoas] = await Promise.all([
          carregarAvaliacoesDoCiclo(nucleo, CICLO),
          carregarAtletas(nucleo).catch(() => []),
        ]);
        if (!ativo) return;

        // A modalidade passa pela normalização ANTES de entrar no estado.
        //
        // O cadastro tem "Tenis de mesa" e "Tênis de mesa" gravados, porque
        // foram digitados em momentos diferentes. Esta tela agrupava pelo
        // texto cru: as duas grafias viravam duas modalidades, o seletor
        // listava a mesma coisa duas vezes e cada filtro pegava metade dos
        // atletas. O gráfico de participação dividia o denominador no meio.
        //
        // Normalizar aqui, na entrada, e não em cada comparação, porque o
        // Excel e o PDF saem deste mesmo estado — corrigir só a tela deixaria
        // o relatório duplicado do mesmo jeito.
        // Sem modalidade cadastrada continua sem: normalizar o vazio criaria
        // uma modalidade "Não informada" no seletor e no gráfico, que é
        // informação nova e não é o que se pediu aqui.
        const comModalidade = (l) => l.map((x) => (
          x.modalidade ? { ...x, modalidade: normalizarModalidade(x.modalidade) } : x
        ));
        setAvaliacoes(comModalidade(respostas));
        setAtletas(comModalidade(pessoas.filter(p => p.grupo === "ATLETA" && p.ativo)));
      } catch (e) {
        if (ativo) setStatus({ tipo: "erro", msg: "Erro ao carregar as avaliações: " + (e.message || "verifique a conexão.") });
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => { ativo = false; };
  }, [nucleo]);

  const modalidades = useMemo(() => {
    const todas = new Set();
    atletas.forEach(a => { if (a.modalidade) todas.add(a.modalidade); });
    avaliacoes.forEach(a => { if (a.modalidade) todas.add(a.modalidade); });
    return [...todas].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [atletas, avaliacoes]);

  const recorte = useMemo(() => {
    const filtra = (lista) => (modalidade ? lista.filter(x => x.modalidade === modalidade) : lista);
    const respostas = filtra(avaliacoes);
    const ativos = filtra(atletas);
    const uidsQueResponderam = new Set(respostas.map(r => r.uid));
    return {
      respostas,
      ativos,
      faltantes: ativos.filter(a => !uidsQueResponderam.has(a.uid)),
      noPrazo: respostas.filter(r => r.noPrazo === true).length,
    };
  }, [avaliacoes, atletas, modalidade]);

  const apuracao = useMemo(() => apurar(recorte.respostas), [recorte.respostas]);

  // Participação por modalidade: respondidas sobre atletas ativos. É o
  // gráfico que diz onde falta cobrar, e por isso mostra as duas barras.
  const porModalidade = useMemo(() => {
    return modalidades.map(m => ({
      modalidade: m,
      responderam: avaliacoes.filter(a => a.modalidade === m).length,
      ativos: atletas.filter(a => a.modalidade === m).length,
    })).filter(x => x.ativos > 0 || x.responderam > 0);
  }, [modalidades, avaliacoes, atletas]);

  const pctParticipacao = recorte.ativos.length > 0
    ? Math.round((recorte.respostas.length / recorte.ativos.length) * 100)
    : null;

  async function baixarExcel() {
    setGerando("excel");
    setStatus({ tipo: "info", msg: "Gerando planilha..." });
    try {
      const nome = await gerarExcelAvaliacao({
        avaliacoes: recorte.respostas,
        nucleo,
        nucleoNome: nomeNucleo(nucleo),
        ciclo: CICLO_ROTULO,
        atletasAtivos: recorte.ativos.length,
      });
      setStatus({ tipo: "ok", msg: `Arquivo "${nome}" gerado e baixado.` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao gerar Excel: " + (e.message || "tente novamente.") });
    } finally {
      setGerando(null);
    }
  }

  async function baixarPDF() {
    setGerando("pdf");
    setStatus({ tipo: "info", msg: "Gerando relatório PDF..." });
    try {
      const nome = await gerarPDFAvaliacaoCiclo({
        apuracao,
        totalRespostas: recorte.respostas.length,
        atletasAtivos: recorte.ativos.length,
        noPrazo: recorte.noPrazo,
        nucleo,
        nucleoNome: nomeNucleo(nucleo),
        ciclo: CICLO_ROTULO,
      });
      setStatus({ tipo: "ok", msg: `Relatório "${nome}" gerado e baixado.` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao gerar PDF: " + (e.message || "tente novamente.") });
    } finally {
      setGerando(null);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── Configurar ──────────────────────────────────────── */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Configurar relatório</div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Sel label="Ciclo" value={CICLO} onChange={() => {}}
            options={[{ value: CICLO, label: `Ciclo ${CICLO_ROTULO}` }]} />
          <Sel label="Modalidade" value={modalidade} onChange={setModalidade}
            options={[{ value: "", label: "Todas as modalidades" },
              ...modalidades.map(m => ({ value: m, label: m }))]} />
        </div>
        <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 12, lineHeight: 1.5 }}>
          O prazo combinado foi {prazoEmBR()}. Quem responde depois continua entrando no
          relatório, marcado como fora do prazo. A avaliação é respondida pelo atleta no
          portal, antes de a Área do Atleta abrir.
        </div>
      </Card>

      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      {carregando && <Card><Carregando linhas={6} /></Card>}

      {!carregando && (
        <>
          {/* ── Números do recorte ──────────────────────────── */}
          <Card
            titulo={`Resultados · Ciclo ${CICLO_ROTULO}`}
            sub={`Núcleo ${nomeNucleo(nucleo) || nucleo}${modalidade ? ` · ${modalidade}` : ""}`}
            acao={
              <>
                <Btn variant="discreto" tamanho="sm" icone="barras"
                  onClick={baixarExcel} disabled={gerando !== null}>
                  {gerando === "excel" ? "Gerando..." : "Excel"}
                </Btn>
                <Btn tamanho="sm" icone="prancheta"
                  onClick={baixarPDF} disabled={gerando !== null}>
                  {gerando === "pdf" ? "Gerando..." : "PDF"}
                </Btn>
              </>
            }
          >
            <FileiraDeEstatisticas minimo={160}>
              <Estatistica valor={recorte.respostas.length} rotulo="Avaliações respondidas"
                contexto={`de ${recorte.ativos.length} atleta(s) ativo(s)`} cor={AZUL} />
              <Estatistica valor={pctParticipacao === null ? "—" : `${pctParticipacao}%`}
                rotulo="Participação"
                cor={pctParticipacao !== null && pctParticipacao >= 80 ? OK : ATENCAO} />
              <Estatistica valor={recorte.noPrazo} rotulo="No prazo" cor={OK} />
              <Estatistica valor={recorte.respostas.length - recorte.noPrazo}
                rotulo="Fora do prazo"
                cor={recorte.respostas.length - recorte.noPrazo > 0 ? ATENCAO : CINZA_ESC} />
            </FileiraDeEstatisticas>

            {porModalidade.length > 0 && (
              <div style={{ marginTop: ESP.xl }}>
                <div style={{ ...TIPO.secao, color: TEXTO, marginBottom: ESP.sm }}>
                  Participação por modalidade
                </div>
                <div style={{ width: "100%", height: Math.max(170, porModalidade.length * 58) }}>
                  <ResponsiveContainer>
                    <BarChart data={porModalidade} layout="vertical"
                      margin={{ top: 4, right: 36, bottom: 4, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#EEF2F7" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="modalidade" width={110} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v, nome) => [v, nome === "responderam" ? "Responderam" : "Atletas ativos"]} />
                      {/* Sem animação: a barra que cresce do zero atrasa a
                          leitura a cada troca de modalidade, e o gráfico
                          aqui é para conferir número, não para apresentar. */}
                      <Bar dataKey="ativos" name="Atletas ativos" fill="#CBD5E1"
                        radius={[0, 4, 4, 0]} isAnimationActive={false} />
                      <Bar dataKey="responderam" name="Responderam" fill={ACAO}
                        radius={[0, 4, 4, 0]} isAnimationActive={false}>
                        <LabelList dataKey="responderam" position="right" style={{ fontSize: 11, fontWeight: 700 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </Card>

          {recorte.respostas.length === 0 ? (
            <Card>
              <Vazio titulo="Nenhuma avaliação respondida">
                Nenhum atleta deste recorte respondeu a avaliação do ciclo até agora.
              </Vazio>
            </Card>
          ) : (
            <>
              {/* ── Distribuição, seção por seção ────────────── */}
              {SECOES.map((secao, i) => {
                const fechadasDaSecao = apuracao.fechadas.filter(f => f.secao === secao.id);
                if (fechadasDaSecao.length === 0) return null;
                return (
                  <Card key={secao.id} titulo={`${i + 1}. ${secao.titulo}`}>
                    <div style={{ display: "flex", flexDirection: "column", gap: ESP.xl }}>
                      {fechadasDaSecao.map(f => <Distribuicao key={f.id} pergunta={f} />)}
                    </div>
                  </Card>
                );
              })}

              {/* ── Respostas escritas ───────────────────────── */}
              <Card titulo="Respostas escritas"
                sub="O que os atletas escreveram, por extenso e sem corte.">
                {apuracao.abertas.every(a => a.respostas.length === 0) ? (
                  <Vazio titulo="Nada escrito ainda">
                    As perguntas abertas são opcionais, e ninguém deste recorte escreveu nelas.
                  </Vazio>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: ESP.xl }}>
                    {apuracao.abertas.map(ab => (
                      <div key={ab.id}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: TEXTO, marginBottom: ESP.sm, lineHeight: 1.45 }}>
                          {ab.texto}
                          <span style={{ ...TIPO.apoio, color: CINZA_ESC, fontWeight: 500, marginLeft: 8 }}>
                            {ab.respostas.length} resposta(s)
                          </span>
                        </div>
                        {ab.respostas.length === 0 ? (
                          <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>Ninguém respondeu esta pergunta.</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: ESP.sm }}>
                            {ab.respostas.map((r, idx) => (
                              <div key={idx} style={{
                                background: FUNDO_SUAVE, border: `1px solid ${BORDA}`,
                                borderRadius: RAIO.md, padding: "11px 13px",
                              }}>
                                <div style={{ fontSize: 11.5, fontWeight: 700, color: CINZA_ESC, marginBottom: 3 }}>
                                  {[r.nome, r.modalidade].filter(Boolean).join(" · ")}
                                </div>
                                <div style={{ ...TIPO.corpo, color: TEXTO, whiteSpace: "pre-line" }}>{r.texto}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {/* ── Quem ainda não respondeu ─────────────────────── */}
          <Card titulo="Ainda não responderam"
            sub="Enquanto não responderem, esses atletas não abrem a Área do Atleta no portal.">
            {recorte.faltantes.length === 0 ? (
              <StatusBox tipo="ok" msg="Todos os atletas ativos deste recorte já responderam." />
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: ESP.sm }}>
                {recorte.faltantes.map(a => (
                  <span key={a.uid} style={{
                    background: BRANCO, border: `1px solid ${BORDA_FORTE}`, borderRadius: RAIO.md,
                    padding: "7px 11px", fontSize: 13, color: TEXTO,
                  }}>
                    {a.nome}
                    <span style={{ color: CINZA_ESC, marginLeft: 6, fontSize: 11.5 }}>{a.modalidade}</span>
                  </span>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ── A distribuição de uma pergunta ────────────────────────────
// Rótulo à esquerda, barra proporcional no meio, quantidade e percentual à
// direita. É a mesma leitura do PDF, para quem imprime e quem olha na tela
// discutirem o mesmo desenho.
function Distribuicao({ pergunta }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: TEXTO, lineHeight: 1.45 }}>
        {pergunta.texto}
      </div>
      <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginBottom: ESP.md }}>
        {pergunta.responderam} resposta(s)
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {pergunta.opcoes.map(o => {
          const cor = corDaOpcao(pergunta.opcoes, o.opcao);
          return (
            <div key={o.opcao} style={{ display: "flex", alignItems: "center", gap: ESP.md }}>
              <div style={{
                width: 150, flexShrink: 0, fontSize: 12.5,
                color: o.neutra ? CINZA_ESC : TEXTO,
                fontStyle: o.neutra ? "italic" : "normal",
              }}>
                {o.opcao}
              </div>
              <div style={{ flex: 1, minWidth: 40, background: NEUTRO_CL, borderRadius: RAIO.pilula, height: 10, overflow: "hidden" }}>
                <div style={{ width: `${o.pct}%`, height: "100%", background: cor, borderRadius: RAIO.pilula }} />
              </div>
              <div style={{
                width: 76, flexShrink: 0, textAlign: "right", fontSize: 12.5,
                fontWeight: o.qtd > 0 ? 700 : 400, color: o.qtd > 0 ? TEXTO : CINZA_ESC,
                fontVariantNumeric: "tabular-nums",
              }}>
                {o.qtd} · {o.pct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
